/** Packaged Capacitor WebView origins (no Vite live-reload port). */
const PACKAGED_PORTS = new Set(["", "80", "443"]);

export function trimBaseUrl(rawBase?: string): string {
  return (rawBase ?? "").trim().replace(/\/+$/, "");
}

/**
 * Same-origin `/api` when `base` is empty (browser `npm run dev` or BFF-served SPA).
 * Device builds must pass an absolute BFF origin — relative paths become
 * `https://localhost/api/...` in the Capacitor WebView and never reach the BFF.
 */
export function resolveApiUrl(path: string, rawBase?: string): string {
  const base = trimBaseUrl(rawBase);
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return base ? `${base}${suffix}` : suffix;
}

export function apiBaseLabel(rawBase?: string): string {
  return trimBaseUrl(rawBase) || "same-origin";
}

/**
 * Browser always uses same-origin `/api` (Vite proxy or BFF-served SPA), even
 * when `.env` / Play builds bake `VITE_API_BASE_URL` to Azure. Otherwise a LAN
 * Vite session (`http://192.168.x.x:5173`) would call the deployed BFF, which
 * may not yet expose campaign tools such as `account_search`.
 * Capacitor native builds keep the baked base so the WebView can reach the BFF.
 */
export function effectiveApiBase(
  rawBase: string | undefined,
  _origin: string | undefined,
  native: boolean,
): string | undefined {
  const baked = trimBaseUrl(rawBase) || undefined;
  if (native) {
    return baked;
  }
  return undefined;
}

export function isPackagedCapacitorOrigin(origin?: string): boolean {
  if (!origin) {
    return true;
  }
  try {
    const url = new URL(origin);
    if (url.hostname !== "localhost" && url.hostname !== "127.0.0.1") {
      return false;
    }
    return PACKAGED_PORTS.has(url.port);
  } catch {
    return true;
  }
}

export function shouldRequireApiBase(
  rawBase: string | undefined,
  origin: string | undefined,
  native: boolean,
): boolean {
  if (trimBaseUrl(rawBase)) {
    return false;
  }
  if (!native) {
    return false;
  }
  return isPackagedCapacitorOrigin(origin);
}

export const MISSING_NATIVE_API_BASE =
  "This Android build has no VITE_API_BASE_URL, so /api calls stay on https://localhost and never reach the BFF. Rebuild with VITE_API_BASE_URL set to a reachable BFF (deployed HTTPS, or npm run cap:android:local).";
